/** @jsxImportSource @emotion/react */
import { Box, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext'
import { Button } from '@app/components/Atoms/Button'
import { Icon } from '@app/components/Atoms/Icon'
import { ImageWithFilters } from '@app/components/Atoms/ImageWithFilters'
import { Link, useNavigate } from 'react-router-dom'
import {
  addLayersButton,
  buttonContainer,
  emptySideDrawerContainer,
  emptySideDrawerContent,
  emptyTitle,
} from './EmptySideDrawer.styles'
import { useFeatureFlags } from '@app/hooks/useFeatureFlags'
import { FEATURE_FLAGS } from '@app/app-constants'

export const EmptySideDrawer = ({
  text,
  type,
  buttonText,
}: {
  text: string
  type: 'select' | 'element' | 'hazard' | 'information'
  buttonText?: string
}) => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { setShowAddLayersModalTab } = useMap()
  const clientName = localStorage.getItem('client_name')
  const { useFeatureFlag } = useFeatureFlags()
  const isGuideEnabled = useFeatureFlag(FEATURE_FLAGS.GUIDE_PAGE)

  if (type === 'select') {
    return (
      <>
        <Box css={emptySideDrawerContainer({ theme })}>
          <Box css={emptySideDrawerContent()}>
            <Icon
              colour={theme.palette.primary.main}
              iconName="s3://select-element.png"
              className="smallicon"
            />
            <Box>
              <Typography variant="subtitle1" css={emptyTitle({ theme })}>
                {text}
              </Typography>
              <Box css={buttonContainer}>
                <Button
                  onClick={() => navigate('/app/guide/tutorials/download-export-and-share-results')}
                  title="Learn how to use the Risk Explorer"
                  variant="outlined"
                >
                  Tutorial
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </>
    )
  }

  return (
    <>
      <Box css={emptySideDrawerContainer({ theme })}>
        <Box css={emptySideDrawerContent()}>
          <ImageWithFilters
            src="/images/illustrations/emptySideDrawer.svg"
            alt="Woman looking for hazards"
            color={theme.palette.primary.main}
            hueStrength={0.9}
            lightenStrength={0.4}
            desaturateStrength={0.4}
            backgroundColor={theme.palette.primary.main + '20'}
            customCss={{
              backgroundColor: theme.palette.background.paper,
              paddingBottom: theme.spacing(4),
              opacity: 0.9,
            }}
          />
          <Box>
            <Typography variant="subtitle1" css={emptyTitle({ theme })}>
              {text}
            </Typography>
            <Box css={buttonContainer}>
              <Button
                size="small"
                variant="contained"
                onClick={() => setShowAddLayersModalTab(type)}
                css={addLayersButton({ theme })}
                color="secondary"
              >
                + {buttonText}
              </Button>
              <Button
                onClick={() => navigate('/app/risk/explorer/workspaces/saved')}
                title="Load Workspace"
                variant="outlined"
                color="secondary"
              >
                Load Workspace
              </Button>

              {clientName && isGuideEnabled && (
                <Box mt={4}>
                  <Typography sx={{ display: 'block', textAlign: 'center', fontSize: 16 }}>
                    Explore our{' '}
                    <Link
                      to={'/guide/tutorials/download-export-and-share-results'}
                      css={{ fontWeight: '600', textDecoration: 'none' }}
                    >
                      Tutorials
                    </Link>
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  )
}
